import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { Plus, Trash2, AlertTriangle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BudgetLimits = () => {
  const [limits, setLimits] = useState([]);
  const [category, setCategory] = useState('');
  const [limitAmount, setLimitAmount] = useState('');
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [limitsRes, categoriesRes] = await Promise.all([
        api.get('/budget-limits'),
        api.get('/categories/expense'),
      ]);
      setLimits(limitsRes.data);
      const categoryNames = categoriesRes.data.map(c => c.name).filter(n => n !== 'Uncategorized');
      setCategories(categoryNames);
      if (categoryNames.length > 0 && !category) {
        setCategory(categoryNames[0]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load budget limits');
    } finally {
      setLoading(false);
    }
  };

  const fetchLimits = async () => {
    try {
      const response = await api.get('/budget-limits');
      setLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch limits:', error);
    }
  };

  const handleCreateLimit = async (e) => {
    e.preventDefault();

    try {
      await api.post('/budget-limits', {
        category,
        limit_amount: parseFloat(limitAmount),
        period,
      });

      toast.success('Budget limit set!');
      setLimitAmount('');
      fetchLimits();
    } catch (error) {
      console.error('Failed to create limit:', error);
      toast.error('Failed to set budget limit');
    }
  };

  const handleDeleteLimit = async (limitId) => {
    try {
      await api.delete(`/budget-limits/${limitId}`);
      toast.success('Limit removed');
      fetchLimits();
    } catch (error) {
      console.error('Failed to delete limit:', error);
      toast.error('Failed to remove limit');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <TrendingUp className="w-8 h-8 text-indigo-400" />
            <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight">
              Budget Limits
            </h1>
          </div>
          <p className="text-lg text-muted-foreground">
            Set spending limits for categories and get alerts when exceeded
          </p>
        </div>

        {/* Create Limit Form */}
        <div className="mb-8 p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl">
          <h2 className="text-xl font-semibold mb-4">Set Budget Limit</h2>
          <form onSubmit={handleCreateLimit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="select-category"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat} className="bg-[#0f172a]">
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Limit Amount
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={limitAmount}
                onChange={(e) => setLimitAmount(e.target.value)}
                data-testid="input-limit-amount"
                placeholder="500.00"
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Period
              </label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                data-testid="select-period"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="weekly" className="bg-[#0f172a]">Weekly</option>
                <option value="monthly" className="bg-[#0f172a]">Monthly</option>
                <option value="yearly" className="bg-[#0f172a]">Yearly</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="submit"
                data-testid="create-limit-button"
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-xl font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Set Limit</span>
              </button>
            </div>
          </form>
        </div>

        {/* Limits List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold mb-4">Active Limits</h2>
          {limits.length > 0 ? (
            limits.map((limit) => (
              <div
                key={limit.id}
                className={`p-6 rounded-2xl border-2 backdrop-blur-xl ${
                  limit.exceeded
                    ? 'bg-red-500/10 border-red-500/50'
                    : limit.percentage > 80
                    ? 'bg-yellow-500/10 border-yellow-500/30'
                    : 'bg-white/5 border-white/10'
                }`}
                data-testid={`limit-${limit.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="text-xl font-semibold">{limit.category}</h3>
                      {limit.exceeded && (
                        <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full flex items-center space-x-1">
                          <AlertTriangle className="w-3 h-3" />
                          <span>EXCEEDED</span>
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground capitalize">
                      {limit.period} limit
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteLimit(limit.id)}
                    data-testid={`delete-limit-${limit.id}`}
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Spent</span>
                    <span className={limit.exceeded ? 'text-red-400 font-bold' : 'font-mono'}>
                      ${limit.current_amount.toFixed(2)} / ${limit.limit_amount.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        limit.exceeded
                          ? 'bg-red-500'
                          : limit.percentage > 80
                          ? 'bg-yellow-500'
                          : 'bg-gradient-to-r from-green-500 to-emerald-500'
                      }`}
                      style={{ width: `${Math.min(limit.percentage, 100)}%` }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>0%</span>
                    <span className={limit.exceeded ? 'text-red-400 font-bold' : ''}>
                      {limit.percentage.toFixed(0)}%
                    </span>
                    <span>100%</span>
                  </div>
                </div>

                {limit.exceeded && (
                  <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400">
                      ⚠️ You've exceeded your {limit.period} budget for {limit.category} by ${(limit.current_amount - limit.limit_amount).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="p-12 text-center text-muted-foreground bg-white/5 border border-white/10 rounded-xl">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No budget limits set yet</p>
              <p className="text-sm mt-2">Create limits to track your spending</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetLimits;
